import { useEffect, useState } from "react";
import { API, formatApiError, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

export default function NewBookingDialog({ open, onOpenChange, onCreated }) {
  const { tenant } = useAuth();
  const tz = tenant?.timezone || "Europe/Paris";
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [employeeId, setEmployeeId] = useState("auto");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    API.get("/services").then((r) => setServices(r.data.filter((s) => s.active)));
    API.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.active)));
    setSlots(null);
    setSlot(null);
  }, [open]);

  useEffect(() => {
    setSlots(null);
    setSlot(null);
    if (serviceId && date) {
      API.get("/slots", { params: { service_id: serviceId, date, employee_id: employeeId } })
        .then((r) => setSlots(r.data))
        .catch(() => setSlots([]));
    }
  }, [serviceId, date, employeeId]);

  const submit = async () => {
    if (!slot) return;
    setSubmitting(true);
    try {
      const localTime = new Date(slot.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz });
      await API.post("/bookings", {
        service_id: serviceId,
        employee_id: employeeId === "auto" ? null : employeeId,
        date: localDateFromTz(slot.start, tz),
        time: localTime,
        ...form,
      });
      toast.success("Rendez-vous créé");
      onOpenChange(false);
      setForm({ first_name: "", last_name: "", phone: "", email: "", notes: "" });
      setSlot(null);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Nouveau rendez-vous</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Service</Label>
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger data-testid="nb-service-select"><SelectValue placeholder="Choisir un service" /></SelectTrigger>
              <SelectContent>
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`nb-service-${s.id}`}>{s.name} ({s.duration_min} min)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Employé</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger data-testid="nb-employee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto" data-testid="nb-employee-auto">Premier disponible</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`nb-employee-${e.id}`}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="nb-date-input" />
          </div>
          {slots && (
            <div>
              <Label>Créneau</Label>
              {slots.length === 0 ? (
                <p className="mt-1 text-sm text-slate-500" data-testid="nb-no-slots">Aucun créneau disponible ce jour.</p>
              ) : (
                <div className="mt-2 grid grid-cols-4 gap-2" data-testid="nb-slots-grid">
                  {slots.map((s) => (
                    <button
                      key={s.start}
                      type="button"
                      onClick={() => setSlot(s)}
                      data-testid={`nb-slot-${fmtTime(s.start, tz)}`}
                      className={`h-10 rounded-md border text-sm font-medium transition-colors ${
                        slot?.start === s.start ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 hover:border-blue-400"
                      }`}
                    >
                      {fmtTime(s.start, tz)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Prénom</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="nb-first-name" />
            </div>
            <div>
              <Label>Nom</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="nb-last-name" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+336..." data-testid="nb-phone" />
            </div>
            <div>
              <Label>Email (facultatif)</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="nb-email" />
            </div>
          </div>
          <Button
            className="w-full"
            disabled={!slot || !form.first_name || !form.last_name || !form.phone || submitting}
            onClick={submit}
            data-testid="nb-submit"
          >
            {submitting ? "Création..." : "Créer le rendez-vous"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function localDateFromTz(iso, tz) {
  const parts = new Intl.DateTimeFormat("fr-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
  return parts; // YYYY-MM-DD
}
